import React, {
    CSSProperties,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    ThumbnailColour,
    ThumbnailColourEdgeLeft,
    ThumbnailColourEdgeRight,
    ThumbnailContainer,
} from 'components/customer/Room/ColorSwatches';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {shallowEqual} from 'react-redux';
import {useAppSelector} from 'store/customer';
import {selectMaterial} from 'components/customer/BTM/store/btmSlice';
import {Styled} from 'shared/types/Styled';
import {BenchtopMaterial} from 'components/customer/BTM/entity/BenchtopMaterial';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {parseHtmlString} from 'shared/helpers/HTMLParser';

const thumbnailSize = 120;
const smallThumbnailSize = 70;
const xsThumbnailSize = 50;

interface MaterialSwatchInterface extends Styled {
    smallPreview?: boolean;
    size?: 'xs' | 'sm' | 'md';
    style?: CSSProperties;
    material?: BenchtopMaterial;
    setDetail?: (event: React.MouseEvent) => void;
    tooltip?: boolean;
}

export const MaterialSwatch = ({
    className,
    size = 'md',
    style,
    material,
    setDetail,
    tooltip = false,
}: MaterialSwatchInterface) => {
    const isSmallDevice = isDeviceSmall();
    const colorThumbnailRef = useRef<HTMLDivElement>();

    const [selectedMaterial, setSelectedMaterial] = useState(material);

    const selectedMaterialBenchtop = useAppSelector(
        selectMaterial,
        shallowEqual
    );

    const previewSize = useMemo(() => {
        if (size == 'md') return thumbnailSize;
        else if (size == 'sm') return smallThumbnailSize;
        else if (size == 'xs') return xsThumbnailSize;
        else return 130;
    }, [size]);

    useEffect(() => {
        if (material == undefined) {
            setSelectedMaterial(selectedMaterialBenchtop);
        }
    }, [material, selectedMaterialBenchtop]);

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            if (typeof setDetail === 'function') {
                setDetail(event);
            }
        },
        [setDetail]
    );

    const thumbnail = (
        <ThumbnailContainer
            onClick={handleClick}
            style={style}
            className={className}
            ref={colorThumbnailRef}
            $size={previewSize}
            $colorImage={selectedMaterial?.image?.name}
            $edgeImage={selectedMaterial?.image?.name}
            $isHorizontal={false}
            $isSmallThumbnail={isSmallDevice}>
            <ThumbnailColourEdgeLeft $isSmallThumbnail={size == 'xs'} />
            <ThumbnailColour />
            <ThumbnailColourEdgeRight $isSmallThumbnail={size == 'xs'} />
        </ThumbnailContainer>
    );

    if (tooltip) {
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <div>
                        {(material && parseHtmlString(material.displayName)) ||
                            ''}
                    </div>
                }>
                {thumbnail}
            </OverlayTrigger>
        );
    }

    return thumbnail;
};
